'use client';

export type PrV2ThreadResolveVariables = {
  commentId: number;
  resolve: boolean;
  threadNodeId: string;
};

type ThreadResolveWaiter = {
  desiredResolved: boolean;
  resolvePromise: () => void;
  rejectPromise: (error: Error) => void;
};

type ThreadResolveEntry = {
  latestVariables: PrV2ThreadResolveVariables;
  desiredResolved: boolean;
  inflightResolved: boolean | null;
  waiters: ThreadResolveWaiter[];
};

const threadResolutionsByPr = new Map<string, Map<string, ThreadResolveEntry>>();

function getOrCreateScope(prScopeKey: string): Map<string, ThreadResolveEntry> {
  let scope = threadResolutionsByPr.get(prScopeKey);
  if (!scope) {
    scope = new Map();
    threadResolutionsByPr.set(prScopeKey, scope);
  }
  return scope;
}

function pruneThreadResolutionScope(prScopeKey: string) {
  const scope = threadResolutionsByPr.get(prScopeKey);
  if (scope?.size === 0) {
    threadResolutionsByPr.delete(prScopeKey);
  }
}

export function upsertThreadResolveIntent(
  prScopeKey: string,
  variables: PrV2ThreadResolveVariables,
): ThreadResolveEntry {
  const scope = getOrCreateScope(prScopeKey);
  const existingEntry = scope.get(variables.threadNodeId);

  if (existingEntry) {
    existingEntry.latestVariables = variables;
    existingEntry.desiredResolved = variables.resolve;
    return existingEntry;
  }

  const entry: ThreadResolveEntry = {
    latestVariables: variables,
    desiredResolved: variables.resolve,
    inflightResolved: null,
    waiters: [],
  };
  scope.set(variables.threadNodeId, entry);
  return entry;
}

export function getThreadResolveIntent(prScopeKey: string, threadNodeId: string) {
  return threadResolutionsByPr.get(prScopeKey)?.get(threadNodeId);
}

export function addThreadResolveWaiter(
  prScopeKey: string,
  threadNodeId: string,
  waiter: ThreadResolveWaiter,
) {
  const entry = getThreadResolveIntent(prScopeKey, threadNodeId);
  if (!entry) {
    waiter.resolvePromise();
    return;
  }

  entry.waiters.push(waiter);
}

export function resolveSupersededThreadResolveWaiters(
  prScopeKey: string,
  threadNodeId: string,
  nextDesiredResolved: boolean,
) {
  const entry = getThreadResolveIntent(prScopeKey, threadNodeId);
  if (!entry) {
    return;
  }

  const nextWaiters: ThreadResolveWaiter[] = [];
  for (const waiter of entry.waiters) {
    if (waiter.desiredResolved === nextDesiredResolved) {
      nextWaiters.push(waiter);
      continue;
    }

    waiter.resolvePromise();
  }

  entry.waiters = nextWaiters;
}

function settleThreadResolveWaiters(
  prScopeKey: string,
  threadNodeId: string,
  desiredResolved: boolean,
  settle: (waiter: ThreadResolveWaiter) => void,
) {
  const entry = getThreadResolveIntent(prScopeKey, threadNodeId);
  if (!entry) {
    return;
  }

  const nextWaiters: ThreadResolveWaiter[] = [];
  for (const waiter of entry.waiters) {
    if (waiter.desiredResolved === desiredResolved) {
      settle(waiter);
      continue;
    }

    nextWaiters.push(waiter);
  }

  entry.waiters = nextWaiters;
}

export function resolveThreadResolveWaiters(
  prScopeKey: string,
  threadNodeId: string,
  desiredResolved: boolean,
) {
  settleThreadResolveWaiters(prScopeKey, threadNodeId, desiredResolved, (waiter) => {
    waiter.resolvePromise();
  });
}

export function rejectThreadResolveWaiters(
  prScopeKey: string,
  threadNodeId: string,
  desiredResolved: boolean,
  error: Error,
) {
  settleThreadResolveWaiters(prScopeKey, threadNodeId, desiredResolved, (waiter) => {
    waiter.rejectPromise(error);
  });
}

export function setThreadResolveInflight(
  prScopeKey: string,
  threadNodeId: string,
  resolved: boolean,
) {
  const entry = getThreadResolveIntent(prScopeKey, threadNodeId);
  if (!entry) {
    return;
  }

  entry.inflightResolved = resolved;
}

export function clearThreadResolveInflight(prScopeKey: string, threadNodeId: string) {
  const entry = getThreadResolveIntent(prScopeKey, threadNodeId);
  if (!entry) {
    return;
  }

  entry.inflightResolved = null;
}

export function removeThreadResolveIntent(prScopeKey: string, threadNodeId: string) {
  const scope = threadResolutionsByPr.get(prScopeKey);
  if (!scope) {
    return;
  }

  scope.delete(threadNodeId);
  pruneThreadResolutionScope(prScopeKey);
}

export function resetThreadResolveStateForTests() {
  threadResolutionsByPr.clear();
}
